import Link from "next/link";

import { Snackbar } from "@chef/components";
import { BRAND_PATHS, ORDER_STATUS_IDS, ORDER_TYPE_IDS } from "@chef/constants";
import { useOrdersQuery } from "@chef/state-management";
import { formatDate } from "@chef/helpers";
import { useCalendarData } from "@chef/state-management/hooks";
import { getCurrentWeekDataAndBasketFromCalendar } from "@chef/state-management/helpers";
import { IPromotion } from "@chef/feature-sanity";

import { intl } from "./CampaignDisclaimer.Intl";

interface CampaignDisclaimerProps {
  week: number;
  year: number;
  promotion?: IPromotion | null;
}

const DeliverySnackbar = ({ promotionSlug }: { promotionSlug: string }) => {
  return (
    <div className="col-span-full lg:col-span-4">
      <Snackbar success className="p-6" showIcon={false}>
        <div className="flex flex-col gap-2">
          <p>
            <strong>{intl.CAMPAIGN_ORDER_BOOKED}</strong>
          </p>
          <p>
            {intl.YOU_CAN_MAKE_CHANGES}
            <Link
              href={BRAND_PATHS.CAMPAIGN_MY_ORDER_PAGE(promotionSlug)}
              className="underline"
            >
              {intl.HERE}
            </Link>
            .
          </p>
        </div>
      </Snackbar>
    </div>
  );
};

const WarningSnackbar = ({
  deliveryDateFormatted,
  promotionSlug,
}: {
  deliveryDateFormatted: string;
  promotionSlug: string;
}) => {
  return (
    <div className="col-span-full lg:col-span-5">
      <Snackbar warning className="p-6" showIcon={false}>
        <div className="flex flex-col gap-2">
          <p>
            <strong>{intl.WARNING}</strong>
          </p>
          <p>
            <span>
              {intl.YOU_HAVE_ORDERED_A_CAMPAIGN_DELIVERY_FOR_THIS_WEEK}{" "}
              {deliveryDateFormatted}. {intl.THIS_ORDER_REMAINS}{" "}
              <strong>{intl.ACTIVE}</strong>.
            </span>
          </p>
          <p>
            {intl.YOU_CAN_MAKE_CHANGES}
            <Link
              href={BRAND_PATHS.CAMPAIGN_MY_ORDER_PAGE(promotionSlug)}
              className="underline"
            >
              {intl.HERE}
            </Link>
            .
          </p>
        </div>
      </Snackbar>
    </div>
  );
};

// const CampaignCTACard = ({ promotionSlug }: { promotionSlug: string }) => (
//   <div className="col-span-full lg:col-span-6">
//     <CTACard
//       title={brand.CAMPAIGN_CTA_CARD_TITLE}
//       buttonLink={BRAND_PATHS.CAMPAIGN_PAGE(promotionSlug)}
//       buttonText={intl.ORDER_HERE}
//       text={brand.CAMPAIGN_CTA_CARD_DESCRIPTION}
//       Image={() => (
//         <Image
//           src={brand.IMAGE_URL || BASE_64_1X1_GREY}
//           layout="fill"
//           objectFit="cover"
//           alt="campaign dish"
//         />
//       )}
//     />
//   </div>
// );

export const CampaignDisclaimer = ({
  week,
  year,
  promotion,
}: CampaignDisclaimerProps) => {
  const { slug, week: weekPromotion } = promotion || {};

  const promotionSlug = slug?.current;

  const { data: ordersQuery } = useOrdersQuery(
    {
      orderTypes: [ORDER_TYPE_IDS.CAMPAIGN],
      orderStatuses: [ORDER_STATUS_IDS.CREATED, ORDER_STATUS_IDS.PROCESSING],
      count: 2,
    },
    { skip: !promotionSlug },
  );

  const { calendar } = useCalendarData({ week, year });

  if (!ordersQuery || !calendar) {
    return null;
  }

  const currentWeekObject = getCurrentWeekDataAndBasketFromCalendar({
    calendar,
    week,
    year,
  })?.mealboxBasket;

  const order = ordersQuery.orders.find(
    (o) => o.week === week && o.year === year,
  );

  if (
    !promotionSlug ||
    !currentWeekObject ||
    (!order && weekPromotion !== week)
  ) {
    return null;
  }

  if (!order && promotion?.week === week) {
    return null;
    // return <CampaignCTACard promotionSlug={promotionSlug} />;
  }

  const hasDelivery = currentWeekObject?.shippingDetails.hasDelivery;
  const deliveryDateFormatted = order
    ? formatDate(order.deliveryDate, undefined, { lowerCase: true })
    : "";

  return hasDelivery ? (
    <DeliverySnackbar promotionSlug={promotionSlug} />
  ) : (
    <WarningSnackbar
      deliveryDateFormatted={deliveryDateFormatted}
      promotionSlug={promotionSlug}
    />
  );
};
